/**
 * Analytics client — fires pageview / identify / conversion beacons to the
 * Sweat With Slay backend. Fully anonymous until the visitor voluntarily
 * identifies themselves (form submit, signup, donation, purchase). Never uses
 * fingerprinting; stores nothing personal in localStorage.
 *
 * IDs:
 *   • visitor_id  — persistent (localStorage). Detects new vs returning.
 *   • session_id  — 30-min-idle (sessionStorage + activity timestamp).
 *   • Both are random UUIDs generated in the browser.
 *
 * All requests use `keepalive: true` and swallow errors so analytics can never
 * block navigation or crash the app.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const VISITOR_KEY = "sws_visitor_id";
const SESSION_KEY = "sws_session_id";
const SESSION_LAST_KEY = "sws_session_last";
const NO_TRACK_KEY = "sws_no_track";
const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 minutes

function uuid() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function safeGet(storage, key) {
  try { return storage.getItem(key) || ""; } catch { return ""; }
}
function safeSet(storage, key, value) {
  try { storage.setItem(key, value); } catch { /* private mode */ }
}
function safeRemove(storage, key) {
  try { storage.removeItem(key); } catch { /* ignore */ }
}

/** Is this device excluded from analytics? Toggled from the Admin panel or by
 *  visiting ?sws_no_track=1. Persists across sessions. */
export function isNoTrack() {
  if (typeof window === "undefined") return true;
  // URL override — visiting ?sws_no_track=1 sets the flag permanently.
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("sws_no_track") === "1") {
      safeSet(window.localStorage, NO_TRACK_KEY, "1");
    }
    if (url.searchParams.get("sws_no_track") === "0") {
      safeRemove(window.localStorage, NO_TRACK_KEY);
    }
  } catch { /* ignore */ }
  return safeGet(window.localStorage, NO_TRACK_KEY) === "1";
}

export function setNoTrack(on) {
  if (typeof window === "undefined") return;
  if (on) safeSet(window.localStorage, NO_TRACK_KEY, "1");
  else safeRemove(window.localStorage, NO_TRACK_KEY);
}

/** Get (or create) the persistent visitor id. Returns "" if window is not available (SSR). */
export function getVisitorId() {
  if (typeof window === "undefined") return "";
  let v = safeGet(window.localStorage, VISITOR_KEY);
  if (!v) {
    v = uuid();
    safeSet(window.localStorage, VISITOR_KEY, v);
  }
  return v;
}

/** Was this visitor here before? (i.e. visitor_id existed before this pageload) */
export function isReturning() {
  if (typeof window === "undefined") return false;
  return !!safeGet(window.localStorage, VISITOR_KEY + "_seen");
}
function markVisitorSeen() {
  if (typeof window === "undefined") return;
  safeSet(window.localStorage, VISITOR_KEY + "_seen", "1");
}

/** Get (or create) the active session id. Resets after 30 minutes of inactivity. */
export function getSessionId() {
  if (typeof window === "undefined") return "";
  const now = Date.now();
  const last = parseInt(safeGet(window.sessionStorage, SESSION_LAST_KEY) || "0", 10);
  let sid = safeGet(window.sessionStorage, SESSION_KEY);
  if (!sid || (last && now - last > SESSION_IDLE_MS)) {
    sid = uuid();
    safeSet(window.sessionStorage, SESSION_KEY, sid);
  }
  safeSet(window.sessionStorage, SESSION_LAST_KEY, String(now));
  return sid;
}

/** Read UTM + gclid params from the current URL. Values persist for the whole session. */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
function readUtmsFromUrl() {
  if (typeof window === "undefined") return {};
  try {
    const u = new URL(window.location.href);
    const out = {};
    UTM_KEYS.forEach((k) => {
      const v = u.searchParams.get(k);
      if (v) out[k] = v;
    });
    return out;
  } catch { return {}; }
}
function getStickyUtms() {
  const fresh = readUtmsFromUrl();
  if (Object.keys(fresh).length) {
    try { window.sessionStorage.setItem("sws_utms", JSON.stringify(fresh)); } catch { /* ignore */ }
    return fresh;
  }
  try {
    const raw = window.sessionStorage.getItem("sws_utms");
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {};
}

/** POST helper — never throws, never blocks. Uses keepalive so beacons survive page unloads. */
async function beacon(path, body) {
  if (!BACKEND) return;
  try {
    await fetch(`${BACKEND}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: "omit",
    });
  } catch { /* silent — analytics must never break UX */ }
}

/** Fire a pageview event. Call this on every route change. */
export function trackPageview(pathname) {
  if (typeof window === "undefined") return;
  if (isNoTrack()) return;
  const session_id = getSessionId();
  const visitor_id = getVisitorId();
  const returning = isReturning();
  markVisitorSeen();
  const utms = getStickyUtms();
  const path = pathname || (window.location.pathname + window.location.search);
  const payload = {
    session_id,
    visitor_id,
    path,
    title: document.title || "",
    referrer: document.referrer || "",
    utm_source: utms.utm_source || "",
    utm_medium: utms.utm_medium || "",
    utm_campaign: utms.utm_campaign || "",
    utm_content: utms.utm_content || "",
    utm_term: utms.utm_term || "",
    screen_w: window.innerWidth || 0,
    screen_h: window.innerHeight || 0,
    is_returning: returning,
    event_type: "pageview",
  };
  beacon("/api/analytics/pageview", payload);
}

/** Link the current session to an email (form submit, signup, purchase, etc). */
export function identify(email, kind, name) {
  if (!email) return;
  if (isNoTrack()) return;
  beacon("/api/analytics/identify", {
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    email,
    kind: kind || "lead",
    name: name || "",
  });
}

/**
 * Return a marketing-attribution snapshot for this visitor.
 * Passed to the server at checkout time so the eventual paid order can be
 * credited to the right traffic source (IG, LinkedIn, email, KMS, QR, etc.)
 * even long after the analytics session has ended.
 */
export function getAttributionSnapshot() {
  if (typeof window === "undefined") return null;
  const utms = getStickyUtms();
  const referrer = document.referrer || "";
  let referrer_domain = "";
  try {
    if (referrer) referrer_domain = new URL(referrer).hostname.replace(/^www\./, "");
  } catch { /* ignore */ }
  return {
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    referrer,
    referrer_domain,
    utm_source: utms.utm_source || "",
    utm_medium: utms.utm_medium || "",
    utm_campaign: utms.utm_campaign || "",
    utm_content: utms.utm_content || "",
    utm_term: utms.utm_term || "",
    landing_path: window.location.pathname || "/",
  };
}

/**
 * Record a conversion event. Common kinds:
 *   lead_form_submit | sacrifice_signup | donation | merch_purchase | account_create
 * `value` is optional (in USD).
 */
export function trackConversion(kind, { value, refId, email } = {}) {
  if (!kind) return;
  if (isNoTrack()) return;
  beacon("/api/analytics/conversion", {
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    kind,
    value: value || 0,
    ref_id: refId || "",
    email: email || "",
  });
}
